import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { ar } from "@/data/translations/ar";
import { en } from "@/data/translations/en";
import { SUPPORTED_LANGUAGES, getLanguageMeta } from "@/data/languages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "safeguarding_translations_v9";
const LANG_KEY = "safeguarding_lang_v1";
const PREFETCH_FLAG = "safeguarding_prefetch_done_v9";

const BUILTIN = { ar, en };

const loadCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveCache = (cache) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore quota errors */
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [cache, setCache] = useState(() => loadCache());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // On mount, prefetch ALL pre-cached translations from the backend into localStorage
  // so subsequent language switches are instant with no network round-trip.
  useEffect(() => {
    let cancelled = false;
    const prefetch = async () => {
      try {
        const res = await axios.get(`${API}/translations/all`, { timeout: 15000 });
        if (cancelled) return;
        const fetched = res.data || {};
        setCache((prev) => {
          const merged = { ...fetched, ...prev }; // local cache wins for any drift
          saveCache(merged);
          return merged;
        });
        localStorage.setItem(PREFETCH_FLAG, "1");
      } catch {
        /* offline-safe — translations will still be fetched on demand */
      }
    };
    if (!localStorage.getItem(PREFETCH_FLAG) || Object.keys(loadCache()).length < 5) {
      prefetch();
    } else {
      // Light refresh once per session in case new languages were added
      prefetch();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const setLang = useCallback(async (code) => {
    setError(null);
    // Built-in or already cached → instant switch
    if (BUILTIN[code] || cache[code]) {
      setLangState(code);
      localStorage.setItem(LANG_KEY, code);
      return;
    }

    // Need to fetch translation from backend
    const meta = getLanguageMeta(code);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/translate`,
        {
          lang_code: code,
          language_name: meta.label,
          source: en,
        },
        { timeout: 90000 }
      );
      const newCache = { ...cache, [code]: res.data.translations };
      setCache(newCache);
      saveCache(newCache);
      setLangState(code);
      localStorage.setItem(LANG_KEY, code);
    } catch (e) {
      setError(e?.response?.data?.detail || "Translation failed. Please try another language.");
    } finally {
      setLoading(false);
    }
  }, [cache]);

  const activeDict = useMemo(() => {
    if (BUILTIN[lang]) return BUILTIN[lang];
    if (cache[lang]) return cache[lang];
    return en; // fallback while loading
  }, [lang, cache]);

  const t = useCallback(
    (key) => {
      const keys = key.split(".");
      let value = activeDict;
      for (const k of keys) {
        if (value === undefined || value === null) return key;
        value = value[k];
      }
      return value ?? key;
    },
    [activeDict]
  );

  const meta = getLanguageMeta(lang);
  const dir = meta.dir;
  const isRTL = dir === "rtl";

  // Sync <html> dir & lang for proper RTL support of native browser features
  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
  }, [dir, lang]);

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        t,
        dir,
        isRTL,
        languages: SUPPORTED_LANGUAGES,
        loading,
        error,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
